import { Injectable } from '@angular/core';

export type SparkModeKey = 'standard' | 'academy' | 'isd_iop';

export const SparkMode = {
  Standard: 0,
  Academy: 1,
  IsdIop: 2,
} as const;

export type SparkModeId = (typeof SparkMode)[keyof typeof SparkMode];

export type SparkModeQueryParams = {
  sparkMode?: number | null;
  academyMode?: number | null;
};

@Injectable({
  providedIn: 'root',
})
export class AcademyModeService {
  private readonly storageKey = 'sparkMode';
  private readonly legacyAcademyStorageKey = 'sparkAcademyMode';

  syncFromCurrentUrl(): boolean {
    this.syncModeFromCurrentUrl();
    return this.isAcademyModeEnabled();
  }

  syncModeFromCurrentUrl(): SparkModeKey {
    const modeFromUrl = this.getSparkModeFromUrl();
    if (modeFromUrl != null) {
      this.setSparkMode(modeFromUrl);
      return modeFromUrl;
    }

    return this.getModeKey();
  }

  isAcademyModeEnabled(): boolean {
    return this.getModeKey() === 'academy';
  }

  isIsdIopModeEnabled(): boolean {
    return this.getModeKey() === 'isd_iop';
  }

  isSeparatedModeEnabled(): boolean {
    return this.getModeKey() !== 'standard';
  }

  getCurrentSparkModeId(): SparkModeId {
    switch (this.getModeKey()) {
      case 'academy':
        return SparkMode.Academy;
      case 'isd_iop':
        return SparkMode.IsdIop;
      default:
        return SparkMode.Standard;
    }
  }

  setAcademyMode(isAcademyMode: boolean): void {
    this.setSparkMode(isAcademyMode ? 'academy' : 'standard');
  }

  setSparkMode(mode: SparkModeKey): void {
    sessionStorage.setItem(this.storageKey, this.modeKeyToId(mode).toString());
    sessionStorage.setItem(
      this.legacyAcademyStorageKey,
      mode === 'academy' ? '1' : '0'
    );
  }

  getModeKey(): SparkModeKey {
    const modeFromUrl = this.getSparkModeFromUrl();
    if (modeFromUrl != null) {
      return modeFromUrl;
    }

    const storedMode = sessionStorage.getItem(this.storageKey);
    if (storedMode != null) {
      return this.modeIdToKey(storedMode);
    }

    return sessionStorage.getItem(this.legacyAcademyStorageKey) === '1'
      ? 'academy'
      : 'standard';
  }

  getAcademyModeQueryParams(): SparkModeQueryParams {
    return this.getSparkModeQueryParams();
  }

  getSparkModeQueryParams(mode: SparkModeKey = this.getModeKey()): SparkModeQueryParams {
    switch (mode) {
      case 'academy':
        return { sparkMode: SparkMode.Academy, academyMode: 1 };
      case 'isd_iop':
        return { sparkMode: SparkMode.IsdIop, academyMode: null };
      default:
        return {};
    }
  }

  getClearedSparkModeQueryParams(): SparkModeQueryParams {
    return { sparkMode: null, academyMode: null };
  }

  appendModeToPath(path: string): string {
    return this.appendModeQuery(path);
  }

  appendModeToApiUrl(url: string): string {
    if (
      this.getModeKey() === 'standard' ||
      url.includes('sparkMode=') ||
      url.includes('academyMode=')
    ) {
      return url;
    }

    return this.appendModeQuery(url);
  }

  getImportButtonLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'Get From IAP';
      case 'isd_iop':
        return 'Get From MH Treatment Plan';
      default:
        return 'Get From BSP';
    }
  }

  getImportButtonId(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'getFromIAPButton';
      case 'isd_iop':
        return 'getFromMHTreatmentPlanButton';
      default:
        return 'getFromBSPButton';
    }
  }

  getCurrentObjectiveLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'IAP Current Objective:';
      case 'isd_iop':
        return 'MH Treatment Plan Objective:';
      default:
        return 'BSP Current Objective:';
    }
  }

  getCurrentObjectivePlaceholder(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'Select an IAP Current Objective';
      case 'isd_iop':
        return 'Select an MH Treatment Plan Objective';
      default:
        return 'Select a BSP Current Objective';
    }
  }

  getObjectiveShortLabel(): string {
    switch (this.getModeKey()) {
      case 'academy':
        return 'IAP Objective:';
      case 'isd_iop':
        return 'MH Treatment Plan Objective:';
      default:
        return 'BSP Objective:';
    }
  }

  getModeDisplayName(mode: SparkModeKey = this.getModeKey()): string {
    switch (mode) {
      case 'academy':
        return 'Academy Mode';
      case 'isd_iop':
        return 'ISD & IOP Mode';
      default:
        return 'Standard Mode';
    }
  }

  private appendModeQuery(url: string): string {
    const modeId = this.getCurrentSparkModeId();
    if (modeId === SparkMode.Standard) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    let result = `${url}${separator}sparkMode=${modeId}`;
    if (modeId === SparkMode.Academy) {
      result += '&academyMode=1';
    }
    return result;
  }

  private getSparkModeFromUrl(): SparkModeKey | null {
    const params = new URLSearchParams(window.location.search);
    const sparkModeFromUrl = params.get('sparkMode');
    if (sparkModeFromUrl != null) {
      return this.modeIdToKey(sparkModeFromUrl);
    }

    const academyModeFromUrl = params.get('academyMode');
    if (academyModeFromUrl == null) {
      return null;
    }

    const normalizedAcademyMode = academyModeFromUrl.toLowerCase();
    return normalizedAcademyMode === '1' || normalizedAcademyMode === 'true'
      ? 'academy'
      : 'standard';
  }

  private modeIdToKey(value: string | number): SparkModeKey {
    const parsed = typeof value === 'number' ? value : Number(value);
    switch (parsed) {
      case SparkMode.Academy:
        return 'academy';
      case SparkMode.IsdIop:
        return 'isd_iop';
      default:
        return 'standard';
    }
  }

  private modeKeyToId(mode: SparkModeKey): SparkModeId {
    switch (mode) {
      case 'academy':
        return SparkMode.Academy;
      case 'isd_iop':
        return SparkMode.IsdIop;
      default:
        return SparkMode.Standard;
    }
  }
}
